import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { partenaireSelectRedux } from '../redux/evenementsServices';
import { formatCompanyName, formatSector } from '../pages/fonctions';

const tooltipBaseStyle = {
  position: 'absolute',
  background: '#ffffff',
  color: '#222222',
  borderRadius: 4,
  padding: '8px 10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  maxWidth: 260,
  pointerEvents: 'auto',
  zIndex: 2,
  cursor: 'pointer',
};

const StandPlanSvg = ({ dataUrl, onLoaded }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector(state => state.user.access);
  const evenementSelected = useSelector(state => state.evenements.evenementSelect);
  const containerRef = useRef(null);
  const loadedRef = useRef(false);
  const touchStartRef = useRef(null);
  const [planData, setPlanData] = useState(null);
  const [error, setError] = useState(null);
  const [navigating, setNavigating] = useState(false);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    stand: null,
  });

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const fetchStart = performance.now();

    const fetchPlan = async () => {
      try {
        const response = await fetch(dataUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Plan data fetch failed (${response.status})`);
        }
        const data = await response.json();
        if (isActive) {
          const fetchDuration = Math.round(performance.now() - fetchStart);
          console.info('[StandPlanSvg] plan data fetch:', fetchDuration, 'ms');
          setPlanData(data);
        }
      } catch (err) {
        if (isActive) {
          setError(err);
        }
      }
    };

    fetchPlan();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [dataUrl]);

  useEffect(() => {
    if (!planData || loadedRef.current) {
      return;
    }

    if (!planData.pictureUrl) {
      loadedRef.current = true;
      if (onLoaded) {
        onLoaded();
      }
      return;
    }

    const imageStart = performance.now();
    const img = new Image();
    img.onload = () => {
      if (!loadedRef.current) {
        const imageDuration = Math.round(performance.now() - imageStart);
        console.info('[StandPlanSvg] plan image load:', imageDuration, 'ms');
        loadedRef.current = true;
        if (onLoaded) {
          onLoaded();
        }
      }
    };
    img.onerror = () => {
      if (!loadedRef.current) {
        const imageDuration = Math.round(performance.now() - imageStart);
        console.info('[StandPlanSvg] plan image load (error):', imageDuration, 'ms');
        loadedRef.current = true;
        if (onLoaded) {
          onLoaded();
        }
      }
    };
    img.src = planData.pictureUrl;
  }, [planData, onLoaded]);

  const highlightStandId = planData?.highlightStandId || null;
  const baseWidth = planData?.baseWidth || 1333;
  const baseHeight = planData?.baseHeight || 824;
  const stands = planData?.stands || [];

  const showTooltip = (event, stand) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    // Handle both mouse and touch events
    let clientX, clientY;
    if (event.touches && event.touches.length > 0) {
      // Touch event
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else if (event.changedTouches && event.changedTouches.length > 0) {
      // Touch end event
      clientX = event.changedTouches[0].clientX;
      clientY = event.changedTouches[0].clientY;
    } else {
      // Mouse event
      clientX = event.clientX;
      clientY = event.clientY;
    }

    setTooltip({
      visible: true,
      x: clientX - rect.left + 12,
      y: clientY - rect.top + 12,
      stand,
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  // Track touch start to distinguish tap from pan
  const handleTouchStart = (event, stand) => {
    if (event.touches.length === 1) {
      touchStartRef.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        stand,
        time: Date.now(),
      };
    }
  };

  // Handle touch end - show tooltip only if it was a tap (not a pan)
  const handleTouchEnd = event => {
    if (!touchStartRef.current) {
      return;
    }

    const touch = event.changedTouches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    const dt = Date.now() - touchStartRef.current.time;

    // Consider it a tap if movement < 10px and duration < 300ms
    if (dx < 10 && dy < 10 && dt < 300) {
      event.stopPropagation();
      event.preventDefault();
      showTooltip(event, touchStartRef.current.stand);
    }

    touchStartRef.current = null;
  };

  const handleTooltipClick = async () => {
    if (tooltip.stand && !tooltip.stand.disponible && tooltip.stand.exposant?.id) {
      const exposantId = tooltip.stand.exposant.id;
      setNavigating(true);
      try {
        const response = await partenaireSelectRedux(accessToken, evenementSelected.id, exposantId);
        dispatch({
          type: 'evenements/addExposantSelect',
          payload: response.data,
        });
        navigate(`/partenaire/${exposantId}/0`);
      } catch (err) {
        console.error('Failed to load exposant details:', err);
        setNavigating(false);
      }
    }
  };

  const tooltipContent = useMemo(() => {
    if (!tooltip.stand) {
      return null;
    }
    if (tooltip.stand.disponible) {
      return (
        <div>
          <strong>Available</strong>
          <div>{tooltip.stand.code}</div>
        </div>
      );
    }
    const exposant = tooltip.stand.exposant;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {exposant?.organisationLogoUrl && (
            <img
              src={exposant.organisationLogoUrl}
              alt=""
              width={32}
              height={32}
              style={{ objectFit: 'contain' }}
            />
          )}
          <div>
            <strong>{formatCompanyName(exposant?.organisationName || '')}</strong>
            <div style={{ fontSize: 12, color: '#555555' }}>
              {formatSector(exposant?.organisationDomaine || '')}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: '#666666' }}>
          {tooltip.stand.salleName ? `${tooltip.stand.salleName} - ` : ''}
          {tooltip.stand.code}
        </div>
        {exposant?.id && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#007bff', fontWeight: 500 }}>
            {navigating ? 'Chargement...' : 'Voir le partenaire →'}
          </div>
        )}
      </div>
    );
  }, [tooltip.stand, navigating]);

  if (error) {
    return <div style={{ padding: 16, color: '#b00020' }}>Unable to load the plan.</div>;
  }

  if (!planData) {
    return null;
  }

  if (!planData.planAvailable) {
    return <div style={{ padding: 16, color: '#b00020' }}>No plan available.</div>;
  }

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onClick={hideTooltip}
      onTouchStart={() => {
        // Hide tooltip when touching elsewhere (will be overridden by stand touch)
        if (tooltip.visible) {
          hideTooltip();
        }
      }}
    >
      {tooltip.visible && (
        <div
          onClick={e => {
            e.stopPropagation();
            handleTooltipClick();
          }}
          style={{
            ...tooltipBaseStyle,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltipContent}
        </div>
      )}
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={4}
        centerOnInit={true}
        wheel={{ step: 0.1 }}
        pinch={{ step: 5 }}
        doubleClick={{ mode: 'toggle', step: 2 }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {/* Zoom controls */}
            <div
              style={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <button
                onClick={() => zoomIn()}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  border: 'none',
                  background: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  fontSize: 20,
                  fontWeight: 'bold',
                }}
                aria-label="Zoom in"
              >
                +
              </button>
              <button
                onClick={() => zoomOut()}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  border: 'none',
                  background: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  fontSize: 20,
                  fontWeight: 'bold',
                }}
                aria-label="Zoom out"
              >
                −
              </button>
              <button
                onClick={() => resetTransform()}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  border: 'none',
                  background: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
                aria-label="Réinitialiser le zoom"
              >
                ↺
              </button>
            </div>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}
            >
              <svg
                viewBox={`0 0 ${baseWidth} ${baseHeight}`}
                preserveAspectRatio="xMidYMid meet"
                width="100%"
                height="100%"
                style={{ display: 'block' }}
              >
                {planData.pictureUrl && (
                  <image
                    href={planData.pictureUrl}
                    x="0"
                    y="0"
                    width={baseWidth}
                    height={baseHeight}
                    preserveAspectRatio="xMidYMid meet"
                  />
                )}
                {stands.map(stand => {
                  if (!stand.width || !stand.height || stand.width <= 0 || stand.height <= 0) {
                    return null;
                  }
                  const isHighlighted = highlightStandId && stand.id === highlightStandId;
                  return (
                    <rect
                      key={stand.id}
                      x={stand.x}
                      y={stand.y}
                      width={stand.width}
                      height={stand.height}
                      fill="transparent"
                      stroke={isHighlighted ? '#f59e0b' : 'transparent'}
                      strokeWidth={isHighlighted ? 3 : 0}
                      style={{ cursor: 'pointer', touchAction: 'none' }}
                      onClick={event => {
                        event.stopPropagation();
                        showTooltip(event, stand);
                      }}
                      onTouchStart={event => handleTouchStart(event, stand)}
                      onTouchEnd={handleTouchEnd}
                    />
                  );
                })}
              </svg>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default StandPlanSvg;
